import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { Logo, GoldButton } from "./Brand";
import { useEnquiry } from "./EnquiryDialog";
import { PROGRAMMES } from "../lib/data";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/courses", label: "Courses", dropdown: true },
  { to: "/payment-options", label: "Payment Options" },
  { to: "/faq", label: "FAQs" },
  { to: "/contact", label: "Contact Us" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [coursesOpen, setCoursesOpen] = useState(false);
  const { openEnquiry } = useEnquiry();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobile(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navClass = ({ isActive }) =>
    `relative font-manrope text-[13px] tracking-[0.12em] uppercase transition-colors duration-300 ${
      isActive ? "text-gold" : "text-white/85 hover:text-gold"
    }`;

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-navy/85 backdrop-blur-xl border-b border-white/10 py-4" : "bg-transparent py-6"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between">
          <Logo />

          <nav className="hidden lg:flex items-center gap-9">
            {links.map((l) =>
              l.dropdown ? (
                <div
                  key={l.to}
                  className="relative"
                  onMouseEnter={() => setCoursesOpen(true)}
                  onMouseLeave={() => setCoursesOpen(false)}
                >
                  <NavLink to={l.to} className={navClass} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
                    <span className="inline-flex items-center gap-1">{l.label} <ChevronDown size={14} strokeWidth={1.5} /></span>
                  </NavLink>
                  <AnimatePresence>
                    {coursesOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 12 }}
                        transition={{ duration: 0.25 }}
                        className="absolute top-full left-1/2 -translate-x-1/2 pt-5 w-72"
                      >
                        <div className="bg-navy-surface border border-gold/20 backdrop-blur-xl p-2">
                          {PROGRAMMES.map((p) => (
                            <Link
                              key={p.id}
                              to={p.path}
                              data-testid={`nav-programme-${p.id}`}
                              className="block px-4 py-3 text-white/80 hover:text-gold hover:bg-white/[0.03] transition-colors group"
                            >
                              <span className="font-playfair text-lg">{p.name}</span>
                              <span className="block text-[11px] tracking-wider uppercase text-white/40 group-hover:text-gold/70">{p.tagline}</span>
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <NavLink key={l.to} to={l.to} end={l.to === "/"} className={navClass} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}>
                  {l.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="hidden lg:block">
            <GoldButton onClick={() => openEnquiry()} data-testid="header-enquire-button">Enquire Now</GoldButton>
          </div>

          <button
            className="lg:hidden text-white"
            onClick={() => setMobile(true)}
            data-testid="mobile-menu-open"
            aria-label="Open menu"
          >
            <Menu strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {mobile && (
          <motion.div
            className="fixed inset-0 z-[60] bg-navy/98 backdrop-blur-xl lg:hidden flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="flex items-center justify-between px-6 py-6 border-b border-white/10">
              <Logo />
              <button onClick={() => setMobile(false)} data-testid="mobile-menu-close" className="text-white" aria-label="Close menu">
                <X strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-col p-8 gap-1">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={({ isActive }) =>
                      `block py-4 font-playfair text-3xl ${isActive ? "text-gold" : "text-white"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <div className="mt-8">
                <GoldButton className="w-full" onClick={() => { setMobile(false); openEnquiry(); }} data-testid="mobile-enquire-button">
                  Enquire Now
                </GoldButton>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
